import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { loadModules } from "esri-loader"; // Import esri-loader

// TODO: Define helper functions outside or inside component as needed (e.g., isDesktop)
// Helper function to convert hex color to RGB array (needed for ESRI v4 symbol colors)
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
    : [0, 0, 0]; // Default to black if parse fails
};

const InteractiveMap = (props) => {
  // Initial state setup using useState based on getInitialState
  const [panelOpen, setPanelOpen] = useState(() => {
    if (props.panel_open !== undefined) {
      return props.panel_open;
    }
    // TODO: Re-implement isDesktop logic if needed, assuming default for now
    // return isDesktop();
    return true; // Defaulting to open for now
  });

  const showInitialFilters = (type) => {
    if (props.selective_filter) {
      return props.map_filters.indexOf(type) !== -1;
    }
    return true;
  };

  const [showTribes, setShowTribes] = useState(() =>
    showInitialFilters("Tribes")
  );
  const [showExperiences, setShowExperiences] = useState(() =>
    showInitialFilters("Experiences")
  );
  const [showAttractions, setShowAttractions] = useState(() =>
    showInitialFilters("Attraction")
  );
  const [showAccommodations, setShowAccommodations] = useState(() =>
    showInitialFilters("Accommodation")
  );

  const [currentFilters, setCurrentFilters] = useState({
    Experiences: [],
    Attractions: [],
    Accommodations: [],
  });
  const [activePointIds, setActivePointIds] = useState([]);
  const [mapPoints, setMapPoints] = useState(props.initial_markers || {});
  const [region, setRegion] = useState(props.region);
  const [searchQuery, setSearchQuery] = useState("");
  const [autoquery, setAutoquery] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Start loading true

  // Ref for the map container div
  const mapDivRef = useRef(null);
  // Ref to hold the MapView instance for cleanup and interaction
  const mapViewRef = useRef(null);
  // Store modules loaded by esri-loader
  const loadedModulesRef = useRef(null);

  // --- Method Conversions ---

  // Convert callApi
  const callApi = async (view) => {
    if (!view || !view.extent) {
      console.warn("Cannot call API without view extent.");
      return;
    }
    setIsLoading(true);
    console.log("Calling API for new bounds...");

    const extent = view.extent;
    // Helper to convert extent points
    const toLatLng = (point) => {
      if (!point) return null;
      // For extent, coordinates are typically already in the map's spatial reference
      // Try different property names based on ArcGIS JS API structure
      if (point.longitude !== undefined && point.latitude !== undefined) {
        return [point.longitude, point.latitude];
      } else if (point.x !== undefined && point.y !== undefined) {
        return [point.x, point.y];
      }
      return null;
    };

    const sw =
      extent.xmin !== undefined && extent.ymin !== undefined
        ? [extent.xmin, extent.ymin].join(",")
        : "";
    const ne =
      extent.xmax !== undefined && extent.ymax !== undefined
        ? [extent.xmax, extent.ymax].join(",")
        : "";
    const center = extent.center
      ? toLatLng(extent.center)?.join(",") || ""
      : "";

    const queryTypes = [];
    if (showTribes) queryTypes.push("Tribe");
    if (showExperiences) queryTypes.push("Experience");
    if (showAttractions) queryTypes.push("Attraction");
    if (showAccommodations) queryTypes.push("Accommodation");
    const typeParam = queryTypes.join("-");

    const apiUrl = `/api/maps/bounds?querytype=${typeParam}&swbounds=${sw}&nebounds=${ne}&center=${center}`;

    try {
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText}`);
      }
      const data = await response.json();
      setMapPoints(data || {}); // Update map points state
      console.log("API call successful, map points updated.");
    } catch (error) {
      console.error("API call failed:", error);
      // Optionally set an error state here
    } finally {
      setIsLoading(false);
    }
  };

  // Placeholder for other methods like clearFilters, setFilter etc.
  const applyState = () => {
    // This function might not be needed directly. Updates trigger re-render
    // which triggers the useEffect for graphics update.
    console.log("Apply state called (likely redundant).");
  };

  // --- Effects ---

  useEffect(() => {
    let view = null; // Define view in outer scope for cleanup
    let modulesLoaded = false; // Flag to track successful loading

    // Define the modules needed
    const moduleNames = [
      "esri/Map",
      "esri/views/MapView",
      "esri/Graphic",
      "esri/layers/GraphicsLayer",
      "esri/core/reactiveUtils",
      "esri/geometry/Point", // Needed for creating point geometry
      "esri/geometry/Polygon", // Needed for creating polygon geometry
      // Add other modules as needed (e.g., symbols, geometryEngine)
    ];

    loadModules(moduleNames, { css: true }) // Load CSS as well
      .then(
        ([
          Map,
          MapView,
          Graphic,
          GraphicsLayer,
          reactiveUtils,
          Point,
          Polygon,
        ]) => {
          modulesLoaded = true;
          loadedModulesRef.current = {
            Map,
            MapView,
            Graphic,
            GraphicsLayer,
            reactiveUtils,
            Point,
            Polygon,
          }; // Store loaded modules

          if (mapDivRef.current) {
            const graphicsLayer = new GraphicsLayer();
            const map = new Map({
              basemap: "national-geographic",
              layers: [graphicsLayer],
            });

            view = new MapView({
              container: mapDivRef.current,
              map: map,
              center: [props.center.lng, props.center.lat],
              zoom: props.zoom,
              constraints: {},
              ui: { components: [] },
            });

            mapViewRef.current = view; // Store view instance

            // Watch for popup closure
            view.popup.watch("selectedFeature", (graphic) => {
              if (!graphic) console.log("Popup closed");
            });

            // Watch for extent changes
            reactiveUtils
              .whenValueChanged(
                () => view?.stationary,
                (stationary) => {
                  if (stationary && autoquery && view) {
                    console.log("Map stationary, autoquery enabled...");
                    callApi(view);
                  }
                }
              )
              .catch((err) =>
                console.error("Error setting up stationary watch:", err)
              );

            view
              .when(() => {
                console.log("MapView initialized via esri-loader");
                setIsLoading(false); // Set loading false
                // Initial graphics load is now handled by the second useEffect
              })
              .catch((err) => {
                console.error("MapView initialization failed: ", err);
                setIsLoading(false); // Set loading false even on error
              });
          } else {
            setIsLoading(false);
          }
        }
      )
      .catch((err) => {
        console.error("Failed to load ArcGIS modules: ", err);
        setIsLoading(false);
      });

    // Cleanup function
    return () => {
      if (mapViewRef.current) {
        // Check mapViewRef instead of local view var
        console.log("Destroying MapView (esri-loader)");
        mapViewRef.current.destroy();
        mapViewRef.current = null;
      }
    };
  }, [props.center, props.zoom, autoquery]); // Keep dependencies

  // TODO: Separate useEffect for updating graphics when mapPoints or filters change
  useEffect(() => {
    // Wait for modules to load AND view to be ready
    if (
      mapViewRef.current &&
      mapViewRef.current.map &&
      loadedModulesRef.current
    ) {
      const { Graphic, GraphicsLayer, Point, Polygon } =
        loadedModulesRef.current; // Get modules from ref
      const graphicsLayer = mapViewRef.current.map.layers.find(
        (layer) => layer instanceof GraphicsLayer
      );

      if (graphicsLayer) {
        graphicsLayer.removeAll();
        console.log("Updating graphics (esri-loader)...");

        // Define colors (can be outside useEffect if static)
        const colors = {
          outline: { hex: "#ffffff" },
          Experience: { hex: "#007da4" },
          Attraction: { hex: "#cb8b19" },
          Accommodation: { hex: "#667f35" },
          Tribe: { hex: "#CA8A18" },
        };

        // Helper functions specific to this effect's logic
        const getColorsFromObject = (mapobj) => {
          let fillColor = colors.Experience.hex;
          if (
            mapobj.type === "Listing" &&
            mapobj.sub_type &&
            colors[mapobj.sub_type]
          ) {
            fillColor = colors[mapobj.sub_type].hex;
          }
          const lineColor = colors.outline.hex;
          return { fillColor, lineColor };
        };

        const typeInFilterArray = (filters, value) => {
          if (!value.themes || !Array.isArray(value.themes)) return false;
          return value.themes.some((theme) => filters.includes(theme));
        };

        const concatFilter = (filterState) => {
          return [
            ...(filterState.Experiences || []),
            ...(filterState.Accommodations || []),
            ...(filterState.Attractions || []),
          ];
        };

        const allFilters = concatFilter(currentFilters);
        const graphicsToAdd = [];
        const addedCoordinates = {};

        Object.values(mapPoints).forEach((value) => {
          let shouldDraw = false;
          // Determine if the point should be drawn based on filters
          if (value.type === "Tribe") {
            shouldDraw = showTribes;
          } else if (value.type === "Experience") {
            shouldDraw =
              showExperiences &&
              (allFilters.length === 0 || typeInFilterArray(allFilters, value));
          } else if (value.type === "Listing") {
            if (value.sub_type === "Attraction") {
              shouldDraw =
                showAttractions &&
                (allFilters.length === 0 ||
                  typeInFilterArray(allFilters, value));
            } else if (value.sub_type === "Accommodation") {
              shouldDraw =
                showAccommodations &&
                (allFilters.length === 0 ||
                  typeInFilterArray(allFilters, value));
            }
          }

          if (shouldDraw) {
            const attributes = { ...value };
            const popupTemplate = {
              title: `<a href="${value.link || "#"}" target="_blank">${
                value.name || "Untitled"
              }</a>`,
              content: `<a href="${
                value.link || "#"
              }" target="_blank"><img src="${
                value.img || props.missingimage
              }" alt="${
                value.name || ""
              } - Image" style="max-width: 100%;"></a>`,
            };

            if (value.type === "Tribe") {
              const tribeSymbol = {
                type: "simple-fill",
                color: [...hexToRgb(colors.Tribe.hex), 0.5],
                outline: { color: hexToRgb(colors.outline.hex), width: 1 },
              };
              if (value.area && value.area.length > 0) {
                value.area.forEach((areaJson) => {
                  try {
                    const rings = JSON.parse(areaJson);
                    // Use loaded Polygon module
                    const polygon = new Polygon({
                      rings: rings,
                      spatialReference: { wkid: 4326 },
                    });
                    // Use loaded Graphic module
                    graphicsToAdd.push(
                      new Graphic({
                        geometry: polygon,
                        symbol: tribeSymbol,
                        attributes: attributes,
                        popupTemplate: popupTemplate,
                      })
                    );
                  } catch (e) {
                    console.error("Failed to parse area JSON:", e);
                  }
                });
              } else if (value.lat != null && value.lng != null) {
                console.warn("Circle geometry for Tribe needs implementation.");
                // Use loaded Point module
                const point = new Point({
                  longitude: value.lng,
                  latitude: value.lat,
                });
                const tribePointSymbol = {
                  type: "simple-marker",
                  style: "triangle",
                  color: hexToRgb(colors.Tribe.hex),
                  size: 12,
                  outline: { color: hexToRgb(colors.outline.hex), width: 1 },
                };
                graphicsToAdd.push(
                  new Graphic({
                    geometry: point,
                    symbol: tribePointSymbol,
                    attributes: attributes,
                    popupTemplate: popupTemplate,
                  })
                );
              }
            } else {
              // Marker (Point)
              if (value.lat != null && value.lng != null) {
                // Use loaded Point module
                const point = new Point({
                  longitude: value.lng,
                  latitude: value.lat,
                });
                const { fillColor, lineColor } = getColorsFromObject(value);
                const markerSize = value.type === "Experience" ? 20 : 15;
                const markerSymbol = {
                  type: "simple-marker",
                  color: hexToRgb(fillColor),
                  size: markerSize,
                  outline: { color: hexToRgb(lineColor), width: 2 },
                };
                const coordKey = `${value.lat},${value.lng}`;
                if (addedCoordinates[coordKey]) {
                  markerSymbol.xoffset =
                    (markerSymbol.size || 15) *
                    1.5 *
                    addedCoordinates[coordKey];
                  addedCoordinates[coordKey]++;
                } else {
                  addedCoordinates[coordKey] = 1;
                }
                graphicsToAdd.push(
                  new Graphic({
                    geometry: point,
                    symbol: markerSymbol,
                    attributes: attributes,
                    popupTemplate: popupTemplate,
                  })
                );
              }
            }
          }
        });
        graphicsLayer.addMany(graphicsToAdd);
        console.log(`Added ${graphicsToAdd.length} graphics.`);
      }
    } else {
      // Log if graphics update is skipped due to view/modules not ready
      // console.log("Skipping graphics update: View or Modules not ready.");
    }
  }, [
    mapPoints,
    currentFilters,
    showTribes,
    showExperiences,
    showAttractions,
    showAccommodations,
    props.missingimage,
  ]); // Dependencies remain

  // Filter-related functions
  const clearFilters = () => {
    setCurrentFilters({
      Experiences: [],
      Attractions: [],
      Accommodations: [],
    });
    setSearchQuery("");
  };

  const toggleFilter = (filterType, filterValue) => {
    setCurrentFilters((prev) => {
      const newFilters = { ...prev };
      const currentArray = newFilters[filterType] || [];

      if (currentArray.includes(filterValue)) {
        newFilters[filterType] = currentArray.filter((f) => f !== filterValue);
      } else {
        newFilters[filterType] = [...currentArray, filterValue];
      }

      return newFilters;
    });
  };

  const toggleAutoQuery = () => {
    setAutoquery(!autoquery);
  };

  const handleSearch = () => {
    // Implement search functionality if needed
    console.log("Search query:", searchQuery);
  };

  // Get filtered results for sidebar display
  const getFilteredResults = () => {
    const results = [];
    const allFilters = [
      ...(currentFilters.Experiences || []),
      ...(currentFilters.Accommodations || []),
      ...(currentFilters.Attractions || []),
    ];

    Object.values(mapPoints).forEach((point) => {
      let shouldShow = false;

      if (point.type === "Tribe" && showTribes) {
        shouldShow = true;
      } else if (point.type === "Experience" && showExperiences) {
        shouldShow =
          allFilters.length === 0 ||
          (point.themes &&
            point.themes.some((theme) => allFilters.includes(theme)));
      } else if (point.type === "Listing") {
        if (point.sub_type === "Attraction" && showAttractions) {
          shouldShow =
            allFilters.length === 0 ||
            (point.themes &&
              point.themes.some((theme) => allFilters.includes(theme)));
        } else if (point.sub_type === "Accommodation" && showAccommodations) {
          shouldShow =
            allFilters.length === 0 ||
            (point.themes &&
              point.themes.some((theme) => allFilters.includes(theme)));
        }
      }

      if (shouldShow) {
        results.push(point);
      }
    });

    return results;
  };

  // Render function conversion
  return (
    <div
      className="esri-interactive-map"
      style={{ position: "relative", width: "100%", height: "100%" }}
    >
      {/* Map container div */}
      <div ref={mapDivRef} style={{ width: "100%", height: "100%" }}></div>

      {/* Loading Spinner */}
      <img
        id="loadingImg"
        src={props.loadingspinner}
        alt="Loading map..."
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 10,
          display: isLoading ? "block" : "none",
        }}
      />

      {/* Sidebar Panel */}
      <div className={`esri-map-sidebar ${panelOpen ? "open" : ""}`}>
        <div className="esri-map-sidebar-menu">
          {/* Search Section */}
          <nav className="esri-map-sidebar-search-nav">
            <div className="esri-map-sidebar-toggle">
              <button
                className="esri-map-clear-button"
                onClick={clearFilters}
                type="button"
              >
                Clear Filters
              </button>
              <button
                className="esri-map-refresh-button"
                onClick={toggleAutoQuery}
                type="button"
              >
                {autoquery ? "Auto Query On" : "Auto Query Off"}
              </button>
            </div>
            <div className="esri-map-sidebar-toggle">
              <input
                type="text"
                className="esri-map-sidebar-search"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
              <i className="fa fa-search" onClick={handleSearch}></i>
            </div>
          </nav>

          {/* Filter Section */}
          <nav className="esri-map-sidebar-menu-nav">
            {/* Experiences Filter */}
            {(props.map_filters.includes("Experiences") ||
              !props.selective_filter) && (
              <div className="esri-btn-refresh-wrapper">
                <button
                  className={`esri-btn-icon ${
                    showExperiences ? "is-active" : ""
                  }`}
                  onClick={() => setShowExperiences(!showExperiences)}
                >
                  <div className="esri-btn-icon">
                    <svg width="40" height="32" viewBox="0 0 40 32">
                      <path
                        className="fill"
                        d="M20 0L25 10H35L27 18L30 28L20 22L10 28L13 18L5 10H15L20 0Z"
                      />
                    </svg>
                  </div>
                  <span
                    className={`switchTitle ${
                      showExperiences ? "is-active" : ""
                    }`}
                  >
                    Experiences
                  </span>
                </button>

                {props.expFilters && (
                  <ul
                    className={`esri-category-wrapper ${
                      showExperiences ? "is-open" : ""
                    }`}
                  >
                    {props.expFilters.map((filter, index) => (
                      <li key={index}>
                        <button
                          className={`esri-filter-button ${
                            currentFilters.Experiences?.includes(filter)
                              ? "is-active"
                              : ""
                          }`}
                          onClick={() => toggleFilter("Experiences", filter)}
                        >
                          <div className="esri-btn-icon">
                            <svg width="40" height="32" viewBox="0 0 40 32">
                              <circle className="fill" cx="20" cy="16" r="8" />
                            </svg>
                          </div>
                          {filter}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Attractions Filter */}
            {(props.map_filters.includes("Attraction") ||
              !props.selective_filter) && (
              <div className="esri-btn-refresh-wrapper">
                <button
                  className={`esri-btn-icon ${
                    showAttractions ? "is-active" : ""
                  }`}
                  onClick={() => setShowAttractions(!showAttractions)}
                >
                  <div className="esri-btn-icon">
                    <svg width="40" height="32" viewBox="0 0 40 32">
                      <rect
                        className="fill"
                        x="8"
                        y="8"
                        width="24"
                        height="16"
                        rx="2"
                      />
                    </svg>
                  </div>
                  <span
                    className={`switchTitle ${
                      showAttractions ? "is-active" : ""
                    }`}
                  >
                    Attractions
                  </span>
                </button>

                {props.attractionsFilters && (
                  <ul
                    className={`esri-category-wrapper ${
                      showAttractions ? "is-open" : ""
                    }`}
                  >
                    {props.attractionsFilters.map((filter, index) => (
                      <li key={index}>
                        <button
                          className={`esri-filter-button ${
                            currentFilters.Attractions?.includes(filter)
                              ? "is-active"
                              : ""
                          }`}
                          onClick={() => toggleFilter("Attractions", filter)}
                        >
                          <div className="esri-btn-icon">
                            <svg width="40" height="32" viewBox="0 0 40 32">
                              <polygon
                                className="fill"
                                points="20,2 6,30 34,30"
                              />
                            </svg>
                          </div>
                          {props.subTypeLabels?.[filter] || filter}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Accommodations Filter */}
            {(props.map_filters.includes("Accommodation") ||
              !props.selective_filter) && (
              <div className="esri-btn-refresh-wrapper">
                <button
                  className={`esri-btn-icon ${
                    showAccommodations ? "is-active" : ""
                  }`}
                  onClick={() => setShowAccommodations(!showAccommodations)}
                >
                  <div className="esri-btn-icon">
                    <svg width="40" height="32" viewBox="0 0 40 32">
                      <path
                        className="fill"
                        d="M5 12L20 2L35 12V26C35 28 33 30 31 30H9C7 30 5 28 5 26V12Z"
                      />
                    </svg>
                  </div>
                  <span
                    className={`switchTitle ${
                      showAccommodations ? "is-active" : ""
                    }`}
                  >
                    Accommodations
                  </span>
                </button>

                {props.accomodationFilter && (
                  <ul
                    className={`esri-category-wrapper ${
                      showAccommodations ? "is-open" : ""
                    }`}
                  >
                    {props.accomodationFilter.map((filter, index) => (
                      <li key={index}>
                        <button
                          className={`esri-filter-button ${
                            currentFilters.Accommodations?.includes(filter)
                              ? "is-active"
                              : ""
                          }`}
                          onClick={() => toggleFilter("Accommodations", filter)}
                        >
                          <div className="esri-btn-icon">
                            <svg width="40" height="32" viewBox="0 0 40 32">
                              <rect
                                className="fill"
                                x="10"
                                y="10"
                                width="20"
                                height="12"
                                rx="1"
                              />
                            </svg>
                          </div>
                          {filter}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Tribes Filter */}
            {(props.map_filters.includes("Tribes") ||
              !props.selective_filter) && (
              <div className="esri-btn-refresh-wrapper">
                <button
                  className={`esri-btn-icon ${showTribes ? "is-active" : ""}`}
                  onClick={() => setShowTribes(!showTribes)}
                >
                  <div className="esri-btn-icon">
                    <svg width="40" height="32" viewBox="0 0 40 32">
                      <polygon className="fill" points="20,4 12,28 28,28" />
                    </svg>
                  </div>
                  <span
                    className={`switchTitle ${showTribes ? "is-active" : ""}`}
                  >
                    Tribes
                  </span>
                </button>
              </div>
            )}
          </nav>

          {/* Results Section */}
          <div className="esri-map-sidebar-results">
            <div style={{ padding: "10px", color: "white", fontSize: "14px" }}>
              Showing {getFilteredResults().length} results
            </div>
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              {getFilteredResults().map((result, index) => (
                <div key={index} className="esri-map-result">
                  <div className="result-type">{result.type}</div>
                  <div className="result-title">{result.name}</div>
                  {result.location && (
                    <div className="result-location">{result.location}</div>
                  )}
                  {result.img && (
                    <img
                      src={result.img}
                      alt={result.name}
                      style={{
                        width: "60px",
                        height: "40px",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Toggle Button */}
      <button
        className="esri-map-toggle-button"
        onClick={() => setPanelOpen(!panelOpen)}
        style={{
          position: "absolute",
          top: "10px",
          left: panelOpen ? "320px" : "10px",
          zIndex: 1000,
          padding: "8px 12px",
          backgroundColor: "rgba(0,0,0,0.8)",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
          transition: "left 300ms ease-in-out",
        }}
      >
        {panelOpen ? "◀" : "▶"}
      </button>
    </div>
  );
};

InteractiveMap.propTypes = {
  panel_open: PropTypes.bool,
  selective_filter: PropTypes.bool,
  map_filters: PropTypes.arrayOf(PropTypes.string),
  initial_markers: PropTypes.object, // TODO: Define shape more precisely if possible
  region: PropTypes.any, // TODO: Define type more precisely
  center: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  zoom: PropTypes.number,
  skip_graphic_extent: PropTypes.bool,
  loadingspinner: PropTypes.string, // Path to loading spinner image
  missingimage: PropTypes.string, // Path to missing image placeholder
  expFilters: PropTypes.array, // e.g., Experience::CATEGORIES
  attractionsFilters: PropTypes.array, // e.g., Attraction::SUB_TYPES
  accomodationFilter: PropTypes.array, // e.g., Accommodation::SUB_TYPES
};

InteractiveMap.defaultProps = {
  panel_open: true, // Default based on isDesktop assumption
  map_filters: [],
  initial_markers: {},
  center: { lat: 39.8283, lng: -98.5795 }, // Default US center
  zoom: 4,
  skip_graphic_extent: false,
  loadingspinner: "/assets/AjaxLoader.gif", // Sensible default?
  missingimage: "/assets/no-photo.jpg", // Sensible default?
  expFilters: [],
  attractionsFilters: [],
  accomodationFilter: [],
};

export default InteractiveMap;
